import { Tag } from '@dev-spendesk/grapes';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { BudgetPeriodTags } from 'src/core/modules/budgets/components/BudgetPeriodTags';
import { BudgetaryExerciseDropdownMenu } from 'src/core/modules/budgets/components/BudgetaryExerciseDropdownMenu';
import { useBudgetaryExerciseModals } from 'src/core/modules/budgets/hooks/useBudgetaryExerciseModals';

import { ExportBudgetButton } from '../../../../components/ExportBudgetButton';
import {
  getBudgetaryExercisePeriod,
  type BudgetaryExercise,
} from '../../../../models/budgetaryExercise';
import { type PeriodRange } from '../../../../models/periodRange';
import { BudgetPeriodSelector } from '../../v1/BudgetPeriodSelector';

export type BudgetaryExerciseActionsBarV2Props = {
  budgetaryExercise: BudgetaryExercise;
  periodRange: PeriodRange;
  setPeriodRange: (periodRange: PeriodRange) => void;
  canEditBudgets: boolean;
};

export const BudgetaryExerciseActionsBarV2 = ({
  budgetaryExercise,
  periodRange,
  setPeriodRange,
  canEditBudgets,
}: BudgetaryExerciseActionsBarV2Props) => {
  const { t } = useTranslation('global');
  const { budgets } = budgetaryExercise;
  const hasBudgets = budgets.length !== 0;

  const { modals, handlers } = useBudgetaryExerciseModals(budgetaryExercise);

  return (
    <div className="flex w-full items-center justify-between gap-8 px-16 py-16">
      {modals.budgetaryExerciseDeletionConfirmationModal}
      {modals.editBudgetExpenseCategoriesModal}
      {modals.budgetaryExerciseEditionModal}

      {/* Left: Exercise name and tags */}
      <div className="flex items-center gap-16">
        <span className="text-content-primary title-m">
          {budgetaryExercise.name}
        </span>

        <Tag variant="neutral">{t('budget.redesigned.budgetV1Tag')}</Tag>
        <BudgetPeriodTags
          period={getBudgetaryExercisePeriod(budgetaryExercise)}
        />
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-8">
        <BudgetPeriodSelector
          range={periodRange}
          maxRange={{
            from: budgetaryExercise.startDate,
            to: budgetaryExercise.endDate,
          }}
          onChange={setPeriodRange}
        />

        <ExportBudgetButton budgetIds={budgets.map((b) => b.id)} />

        {canEditBudgets && (
          <BudgetaryExerciseDropdownMenu
            hasBudgets={hasBudgets}
            onEdit={handlers.showEdit}
            onDelete={handlers.showDelete}
            onUpdateBudgets={handlers.showUpdateBudgets}
          />
        )}
      </div>
    </div>
  );
};
